<script setup lang="ts">
import { h, computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  NLayout,
  NLayoutSider,
  NLayoutContent,
  NMenu,
  NAvatar,
  NDropdown,
  NIcon,
  NDrawer,
  NDrawerContent,
  NButton,
  NBadge
} from 'naive-ui'
import financeApi from '@/api/finance'
import adminNavigationApi from '@/api/adminNavigation'
import { useAdminAttention } from '@/composables/useAdminAttention'
import type { AttentionKey } from '@/api/attention'
import { reportsApi } from '@/api/reports'
import {
  HomeOutline,
  PeopleOutline,
  LogOutOutline,
  PersonOutline,
  MenuOutline,
  CloseOutline,
  FlagOutline,
  BanOutline,
  WalletOutline,
  AppsOutline,
  TvOutline,
  TrophyOutline,
  PodiumOutline,
  ShirtOutline,
  ChatbubblesOutline,
  BarbellOutline
} from '@vicons/ionicons5'
import fieldIcon from '@/assets/icons/field2.svg'
import stadiumIcon from '@/assets/icons/stadium_new.svg'
import whistleIcon from '@/assets/icons/whistle.svg'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const attention = useAdminAttention()
const mobileMenuOpen = ref(false)
const siderCollapsed = ref(false)
const appVersion = __APP_VERSION__

const renderIcon = (icon: any) => () => h(NIcon, null, { default: () => h(icon) })
const renderSvgIcon = (src: string) => () => h('img', { src, style: 'width: 20px; height: 20px; filter: brightness(0) invert(1); opacity: 0.85' })

// Бейджи навигации: денежные очереди, обращения и новые операционные задачи.
// Они не меняют права доступа и лишь подсказывают, куда нужно зайти. Поллинг
// раз в минуту намеренно дешёвый: сервер возвращает только агрегаты.
const badges = ref({
  pending_payouts: 0,
  negative_balances: 0,
  referee_escrow_anomalies: 0,
  financial_queues: 0,
  pending_reports: 0,
  pending_coach_applications: 0,
  pending_referee_applications: 0,
  pending_championship_moderation: 0,
  pending_device_reports: 0,
  new_stadiums: 0,
  new_kit_orders: 0,
  referee_no_show_disputes: 0,
  pending_closed_clubs: 0,
  unread_support_conversations: 0
})
let badgesTimer: number | undefined
async function loadBadges() {
  void attention.refreshAttention()
  try {
    const fin = await financeApi.getBadges()
    badges.value = { ...badges.value, ...fin }
  } catch { /* тихо — бейджи не критичны */ }
  try {
    const stats = await reportsApi.getStats()
    badges.value = { ...badges.value, pending_reports: stats.data?.pending || 0 }
  } catch { /* тихо */ }
  try {
    const navigation = await adminNavigationApi.getBadges()
    badges.value = { ...badges.value, ...navigation }
  } catch { /* тихо — основная навигация доступна без счётчиков */ }
}
function labelWithBadge(text: string, count: number, attentionKeys: AttentionKey[] = []) {
  const attentionItems = attention.available.value === true
    ? attention.items.value.filter(item => attentionKeys.includes(item.key))
    : []
  const newCount = attentionItems.reduce((sum, item) => sum + item.new_count, 0)
  const overdueCount = attentionItems.reduce((sum, item) => sum + item.overdue_count, 0)
  const pendingStadiumCount = attentionItems.filter(item => item.key === 'new_stadiums').reduce((sum, item) => sum + item.count, 0)
  const badgeCount = attention.available.value === true
    ? (attentionKeys.includes('new_stadiums') ? pendingStadiumCount : (newCount || overdueCount)) : count
  if (!badgeCount) return text
  return () => h('div', { style: 'display:flex;align-items:center;gap:8px' }, [
    h('span', text),
    h(NBadge, {
      value: badgeCount,
      type: overdueCount > 0 ? 'error' : 'warning',
      max: 99
    })
  ])
}
const refreshBadges = () => { void loadBadges() }
onMounted(() => {
  loadBadges()
  badgesTimer = window.setInterval(loadBadges, 60000)
  window.addEventListener('admin:badges-refresh', refreshBadges)
})
onUnmounted(() => {
  if (badgesTimer) window.clearInterval(badgesTimer)
  window.removeEventListener('admin:badges-refresh', refreshBadges)
})

const financeBadgeTotal = computed(() => badges.value.pending_payouts +
  badges.value.negative_balances + badges.value.referee_escrow_anomalies +
  badges.value.financial_queues + (authStore.isAdmin ? badges.value.pending_closed_clubs : 0))
const financeAttentionKeys: AttentionKey[] = [
  'referee_no_show', 'referee_manual_review', 'referee_escrow', 'payouts_pending',
  'closed_clubs', 'leak_control', 'negative_balances'
]

const menuOptions = computed(() => [
  {
    label: 'Главная',
    key: 'dashboard',
    icon: renderIcon(HomeOutline)
  },
  {
    label: labelWithBadge('Финансы', financeBadgeTotal.value, financeAttentionKeys),
    key: 'finance-menu',
    icon: renderIcon(WalletOutline),
    children: [
      { label: 'Аналитика', key: 'analytics' },
      { label: 'Кошельки', key: 'wallets' },
      { label: 'Транзакции и платежи', key: 'finance' },
      { label: 'Движение денег', key: 'finance-custody' },
      { label: 'Реестр движений', key: 'finance-ledger' },
      { label: labelWithBadge('Выплаты', badges.value.pending_payouts, ['payouts_pending']), key: 'payouts' },
      // Модератору раздел недоступен (сервер отвечает 403 на сводку).
      ...(authStore.isAdmin ? [{
        label: labelWithBadge('Разбор закрытых клубов', badges.value.pending_closed_clubs, ['closed_clubs']),
        key: 'club-closure'
      }] : []),
      {
        label: labelWithBadge(
          'Контроль утечек',
          badges.value.negative_balances + badges.value.referee_escrow_anomalies,
          ['negative_balances', 'referee_escrow', 'leak_control']
        ),
        key: 'finance-anomalies'
      },
      { label: labelWithBadge('Финансовые очереди', badges.value.financial_queues, ['referee_no_show', 'referee_manual_review']), key: 'finance-queues' },
      { label: 'Управление тарифами', key: 'tariffs' },
      { label: 'Финансовая сводка', key: 'tariff-finance' },
      {
        label: 'Бонусы',
        key: 'bonuses-submenu',
        children: [
          { label: 'Шаблоны', key: 'bonuses' },
          { label: 'Правила выдачи', key: 'bonus-rules' },
          { label: 'Достижения', key: 'achievements' },
          { label: 'Уровни клубов', key: 'club-levels' },
          { label: 'Список бонусов', key: 'bonuses-list' },
          { label: 'Выдать бонус', key: 'bonuses-grant' },
          { label: 'Рефералы', key: 'referrals' },
          { label: 'Статистика', key: 'statistics' }
        ]
      }
    ]
  },
  {
    label: 'Матчи',
    key: 'matches',
    icon: renderSvgIcon(fieldIcon)
  },
  {
    label: labelWithBadge('Чемпионаты', badges.value.pending_championship_moderation, ['championship_moderation']),
    key: 'championships',
    icon: renderIcon(TrophyOutline)
  },
  {
    label: 'Рейтинги',
    key: 'ratings-menu',
    icon: renderIcon(PodiumOutline),
    children: [
      { label: 'Рейтинг клубов', key: 'club-ratings' },
      { label: 'Рейтинг игроков', key: 'player-ratings' }
    ]
  },
  {
    label: labelWithBadge('Стадионы', badges.value.new_stadiums, ['new_stadiums']),
    key: 'stadiums-menu',
    icon: renderSvgIcon(stadiumIcon),
    children: [
      { label: labelWithBadge('Заявки', badges.value.new_stadiums, ['new_stadiums']), key: 'stadium-applications' },
      { label: 'Все стадионы', key: 'stadiums' },
      { label: 'Размеры полей', key: 'field-sizes' }
    ]
  },
  {
    label: labelWithBadge('Поддержка', badges.value.unread_support_conversations, ['support']),
    key: 'support-menu',
    icon: renderIcon(ChatbubblesOutline),
    children: [
      { label: 'Тикеты', key: 'support' },
      { label: labelWithBadge('Жалобы', badges.value.pending_reports, ['complaints']), key: 'reports', icon: renderIcon(FlagOutline) }
    ]
  },
  {
    label: labelWithBadge('Формы', badges.value.new_kit_orders, ['kit_orders']),
    key: 'kits-menu',
    icon: renderIcon(ShirtOutline),
    children: [
      { label: labelWithBadge('Заказы', badges.value.new_kit_orders, ['kit_orders']), key: 'kit-orders' },
      { label: 'Каталог форм', key: 'kit-presets' }
    ]
  },
  {
    label: labelWithBadge('Рефери', badges.value.pending_referee_applications, ['referee_applications']),
    key: 'referees-menu',
    icon: renderSvgIcon(whistleIcon),
    children: [
      { label: 'Список рефери', key: 'referees' },
      { label: labelWithBadge('Заявки', badges.value.pending_referee_applications, ['referee_applications']), key: 'referee-applications' },
      { label: 'Ставки', key: 'referee-rates' }
    ]
  },
  {
    label: labelWithBadge('Тренеры', badges.value.pending_coach_applications, ['coach_applications']),
    key: 'coaches-menu',
    icon: renderIcon(BarbellOutline),
    children: [
      { label: 'Список тренеров', key: 'coaches' },
      { label: labelWithBadge('Заявки', badges.value.pending_coach_applications, ['coach_applications']), key: 'coach-applications' },
      { label: 'Тренерские клубы', key: 'training-clubs' }
    ]
  },
  {
    label: 'Пользователи',
    key: 'users-menu',
    icon: renderIcon(PeopleOutline),
    children: [
      { label: 'Список пользователей', key: 'users', icon: renderIcon(PeopleOutline) },
      { label: 'Заблокированные', key: 'banned-users', icon: renderIcon(BanOutline) }
    ]
  },
  {
    label: labelWithBadge('iGo Live', badges.value.pending_device_reports, ['equipment_reports']),
    key: 'igo-live-menu',
    icon: () => h('div', {
      style: 'width: 22px; height: 22px; border-radius: 6px; background: #ef4444; display: flex; align-items: center; justify-content: center'
    }, h(NIcon, { size: 14, color: '#fff' }, { default: () => h(TvOutline) })),
    children: [
      { label: 'Устройства', key: 'devices' },
      ...(authStore.isAdmin ? [{ label: 'Метки (датчики)', key: 'sensor-tags' }] : []),
      { label: 'AI Pipeline', key: 'ai-pipeline' },
      { label: 'Активные пайплайны', key: 'active-pipelines' },
      { label: 'AI Модели', key: 'ai-models' },
      { label: 'Реклама', key: 'advertisements' },
      { label: 'Релизы', key: 'apps-releases' },
      { label: 'API ключи', key: 'api-keys' },
      { label: 'Журналы', key: 'logs' },
      { label: labelWithBadge('Репорты об оборудовании', badges.value.pending_device_reports, ['equipment_reports']), key: 'device-reports' }
    ]
  },
  ...(authStore.isAdmin ? [{
    label: () => h('span', { style: 'color: #fff; font-weight: 600' }, '🤖 iGo Bot'),
    key: 'bot-settings',
    icon: () => h('div', {
      style: 'width: 22px; height: 22px; border-radius: 6px; background: #8b5cf6; display: flex; align-items: center; justify-content: center; font-size: 14px'
    }, '🤖')
  }, {
    label: 'Журнал помощника',
    key: 'assistant-journal',
    icon: () => h('div', {
      style: 'width: 22px; height: 22px; border-radius: 6px; background: #0ea5e9; display: flex; align-items: center; justify-content: center; font-size: 14px'
    }, '🎙')
  }] : []),
  {
    label: 'Приложения',
    key: 'apps',
    icon: renderIcon(AppsOutline)
  }
])

const userMenuOptions = [
  { label: 'Профиль', key: 'profile', icon: renderIcon(PersonOutline) },
  { type: 'divider', key: 'd1' },
  { label: 'Выйти', key: 'logout', icon: renderIcon(LogOutOutline) }
]

const activeKey = computed(() => route.name === 'stadiums' && route.query.tab === 'applications'
  ? 'stadium-applications' : route.name === 'stadium-detail' ? 'stadiums' : route.name as string)

function handleMenuSelect(key: string) {
  if (key === 'stadium-applications') router.push({ name: 'stadiums', query: { tab: 'applications' } })
  else router.push({ name: key })
  mobileMenuOpen.value = false
}

function handleUserMenuSelect(key: string) {
  if (key === 'logout') {
    authStore.logout()
    router.push({ name: 'login' })
  } else if (key === 'profile') {
    router.push({ name: 'profile' })
  }
}

const roleLabels: Record<string, string> = {
  super_admin: 'Супер-админ',
  admin: 'Админ',
  moderator: 'Модератор'
}

</script>

<template>
  <NLayout has-sider class="admin-root">
    <!-- Desktop Sidebar -->
    <NLayoutSider
      v-model:collapsed="siderCollapsed"
      bordered
      collapse-mode="width"
      :collapsed-width="64"
      :width="240"
      show-trigger
      class="bg-gray-900 hidden md:block"
    >
      <div class="flex items-center justify-center h-16 border-b border-gray-700">
        <img src="/log.png" alt="iGo" class="h-10 w-10" />
        <span v-show="!siderCollapsed" class="ml-2 text-xl font-bold text-white">iGo Admin</span>
      </div>
      
      <NMenu
        :value="activeKey"
        :options="menuOptions"
        :collapsed-width="64"
        :collapsed-icon-size="22"
        @update:value="handleMenuSelect"
      />

      <div class="sider-version" :class="{ 'sider-version--collapsed': siderCollapsed }">
        <span v-if="!siderCollapsed">iGo Admin v{{ appVersion }}</span>
        <span v-else>v{{ appVersion }}</span>
      </div>
    </NLayoutSider>
    
    <!-- Mobile Drawer Menu -->
    <NDrawer v-model:show="mobileMenuOpen" placement="left" :width="280" class="md:hidden">
      <NDrawerContent body-content-style="padding: 0; background: #111827">
        <div class="flex items-center justify-between h-16 px-4 border-b border-gray-700 bg-gray-900">
          <div class="flex items-center">
            <img src="/log.png" alt="iGo" class="h-8 w-8" />
            <span class="ml-2 text-lg font-bold text-white">iGo Admin</span>
          </div>
          <NButton quaternary circle @click="mobileMenuOpen = false">
            <template #icon>
              <NIcon :component="CloseOutline" />
            </template>
          </NButton>
        </div>
        
        <div class="mobile-menu-scroll">
          <NMenu
            :value="activeKey"
            :options="menuOptions"
            @update:value="handleMenuSelect"
          />
        </div>

        <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700 bg-gray-900">
          <div class="flex items-center gap-3">
            <NAvatar round size="small" :src="authStore.user?.image">
              {{ authStore.user?.username?.[0]?.toUpperCase() }}
            </NAvatar>
            <div class="text-sm flex-1">
              <div class="text-white">{{ authStore.user?.username }}</div>
              <div class="text-gray-400 text-xs">{{ roleLabels[authStore.user?.admin_role || ''] }}</div>
            </div>
          </div>
          <NButton block tertiary class="mt-3" @click="handleUserMenuSelect('logout')">
            <template #icon>
              <NIcon :component="LogOutOutline" />
            </template>
            Выйти
          </NButton>
        </div>
      </NDrawerContent>
    </NDrawer>
    
    <NLayout class="layout-with-header">
      <div class="admin-header">
        <!-- Mobile burger button -->
        <NButton quaternary circle class="md:hidden" @click="mobileMenuOpen = true">
          <template #icon>
            <NIcon :component="MenuOutline" size="24" />
          </template>
        </NButton>
        
        <h1 class="text-base md:text-lg font-semibold text-white truncate flex-1 ml-2 md:ml-0">
          {{ route.meta.title || 'iGo Admin' }}
        </h1>
        
        <NDropdown
          :options="userMenuOptions"
          @select="handleUserMenuSelect"
        >
          <div class="flex items-center gap-3 cursor-pointer hover:bg-gray-700 px-2 md:px-3 py-2 rounded-lg">
            <NAvatar
              round
              size="small"
              :src="authStore.user?.image"
            >
              {{ authStore.user?.username?.[0]?.toUpperCase() }}
            </NAvatar>
            <div class="text-sm hidden md:block">
              <div class="text-white">{{ authStore.user?.username }}</div>
              <div class="text-gray-400 text-xs">{{ roleLabels[authStore.user?.admin_role || ''] }}</div>
            </div>
          </div>
        </NDropdown>
      </div>
      
      <NLayoutContent class="admin-content">
        <slot />
      </NLayoutContent>
    </NLayout>
  </NLayout>
</template>

<style scoped>
/* 100dvh: на iOS 100vh больше видимой области — низ страницы уезжал под
   панель браузера. vh остаётся фолбэком для браузеров без dvh. */
.admin-root {
  height: 100vh;
  height: 100dvh;
}

.layout-with-header {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
}

.admin-header {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 56px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgb(55, 65, 81);
  background-color: rgb(31, 41, 55);
}

@media (min-width: 768px) {
  .admin-header {
    height: 64px;
    padding: 0 1.5rem;
  }
}

.admin-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  background-color: rgb(17, 24, 39);
}

/* Mobile drawer menu: make it scrollable and leave room for the fixed user card at the bottom */
.mobile-menu-scroll {
  height: calc(100vh - 64px); /* minus header */
  height: calc(100dvh - 64px);
  overflow-y: auto;
  padding-bottom: 140px; /* leave room for the fixed user card + logout button */
  -webkit-overflow-scrolling: touch;
}

/* Desktop sidebar: also give menu breathing room at the bottom */
:deep(.n-layout-sider .n-menu) {
  padding-bottom: 48px;
}

/* Версия в самом низу сайдбара. Sticky внутри Sider, чтобы при коллапсе
   всё ужималось и оставалась только короткая 'v1.2.3'. */
.sider-version {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 12px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  border-top: 1px solid rgb(55, 65, 81);
  background: rgb(17, 24, 39);
  user-select: none;
}
.sider-version--collapsed {
  padding: 8px 4px;
  font-size: 10px;
}

@media (min-width: 768px) {
  .admin-content {
    padding: 1.5rem;
  }
}

/* Мобила: реально прокручивается только контейнер .layout-with-header. Внутренние
   overflow:auto у NLayoutContent не скроллятся (высота = контент), но становятся
   ближайшим «скроллером» для position: sticky — и липкая лента разделов на
   странице устройства уезжала вместе с контентом. Делаем их прозрачными. */
@media (max-width: 767px) {
  .admin-content,
  .admin-content > :deep(.n-layout-scroll-container) {
    overflow: visible !important;
  }
}

</style>
